import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as signalR from "@microsoft/signalr";
import { axiosPrivate } from "./Axios";
import { useAuth } from "./AuthProvider";

const ContactDataHubContext = createContext(null);

export function ContactDataHubProvider({ children }) {
  const { cookies } = useAuth();

  const tokenRef = useRef(cookies.jwtToken);
  useEffect(() => {
    tokenRef.current = cookies.jwtToken;
  }, [cookies.jwtToken]);

  const [status, setStatus] = useState("disconnected");
  const [lastUpdated, setLastUpdated] = useState(null);

  const connectionRef = useRef(null);
  const joinedGroupsRef = useRef(new Set());
  const listenersRef = useRef(new Map());

  useEffect(() => {
    const joinedGroups = joinedGroupsRef.current;
    const listeners = listenersRef.current;

    const backendUrl = (axiosPrivate.defaults.baseURL || "").replace(
      /\/+$/,
      "",
    );

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${backendUrl}/hubs/contactdata`, {
        accessTokenFactory: () => tokenRef.current,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connection.onreconnecting(() => {
      setStatus("reconnecting");
    });

    connection.onreconnected(async () => {
      setStatus("connected");
      for (const csId of joinedGroupsRef.current) {
        try {
          await connection.invoke("JoinSiteGroup", csId);
        } catch (err) {
        }
      }
    });

    connection.onclose(() => {
      setStatus("disconnected");
    });

    connection.on("SiteDataUpdated", (nudge) => {
      setLastUpdated(new Date());
      const handlers = listenersRef.current.get("SiteDataUpdated");
      if (!handlers) return;
      for (const fn of handlers) {
        try {
          fn(nudge);
        } catch (err) {
        }
      }
    });

    setStatus("connecting");
    connection
      .start()
      .then(() => {
        setStatus("connected");
      })
      .catch(() => {
        setStatus("disconnected");
      });

    connectionRef.current = connection;

    return () => {
      connection.stop().catch(() => {});
      connectionRef.current = null;
      joinedGroups.clear();
      listeners.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const joinGroups = useCallback(async (csIds) => {
    const connection = connectionRef.current;
    if (!connection) return;
    if (connection.state !== signalR.HubConnectionState.Connected) return;
    for (const csId of csIds ?? []) {
      if (!csId || joinedGroupsRef.current.has(csId)) continue;
      try {
        await connection.invoke("JoinSiteGroup", csId);
        joinedGroupsRef.current.add(csId);
      } catch (err) {
      }
    }
  }, []);

  const leaveGroups = useCallback(async (csIds) => {
    const connection = connectionRef.current;
    if (!connection) return;
    if (connection.state !== signalR.HubConnectionState.Connected) return;
    for (const csId of csIds ?? []) {
      if (!csId || !joinedGroupsRef.current.has(csId)) continue;
      try {
        await connection.invoke("LeaveSiteGroup", csId);
        joinedGroupsRef.current.delete(csId);
      } catch (err) {
      }
    }
  }, []);

  // Returns an unsubscribe function suitable for useEffect cleanup.
  const subscribe = useCallback((event, handler) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(handler);
    return () => {
      const set = listenersRef.current.get(event);
      if (set) set.delete(handler);
    };
  }, []);

  const value = useMemo(
    () => ({ status, lastUpdated, joinGroups, leaveGroups, subscribe }),
    [status, lastUpdated, joinGroups, leaveGroups, subscribe],
  );

  return (
    <ContactDataHubContext.Provider value={value}>
      {children}
    </ContactDataHubContext.Provider>
  );
}

export function useContactDataHub() {
  const ctx = useContext(ContactDataHubContext);
  if (!ctx) {
    throw new Error(
      "useContactDataHub must be used inside <ContactDataHubProvider>",
    );
  }
  return ctx;
}
